// src/services/geocodingService.js
// Geocodificacion (Nominatim) + distancia por carretera (OSRM), ambos
// servicios publicos y gratuitos, sin API key. Compartido entre la pagina
// de Venues (mapa) y el calculo de km/precio del formulario de eventos.

// Almacen de Erandio (punto de salida del material).
export const ORIGIN = { lat: 43.297369, lng: -2.942242 };

const geoCache = {};

/**
 * Geocodifica una direccion/query de texto libre a { lat, lng }.
 * Cachea en memoria por query exacta para no repetir llamadas.
 * @param {string} query
 * @returns {Promise<{lat:number, lng:number}|null>}
 */
export async function geocodeAddress(query) {
  const q = String(query || "").trim();
  if (!q) return null;
  if (geoCache[q] !== undefined) return geoCache[q];

  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`);
    if (!res.ok) {
      console.warn(`[geocoding] Nominatim respondio ${res.status} para "${q}"`);
    } else {
      const data = await res.json();
      if (data && data.length > 0) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        geoCache[q] = coords;
        return coords;
      }
      console.warn(`[geocoding] Nominatim no encontro resultados para "${q}"`);
    }
  } catch (e) {
    console.warn(`[geocoding] Error geocodificando "${q}":`, e);
  }

  geoCache[q] = null;
  return null;
}

/**
 * Distancia de ruta por carretera (km) entre dos puntos, via OSRM publico.
 * @param {{lat:number,lng:number}} origin
 * @param {{lat:number,lng:number}} destination
 * @returns {Promise<number|null>}
 */
export async function getDrivingDistanceKm(origin, destination) {
  if (!origin || !destination) return null;
  if (!Number.isFinite(origin.lat) || !Number.isFinite(origin.lng)) return null;
  if (!Number.isFinite(destination.lat) || !Number.isFinite(destination.lng)) return null;

  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=false`;
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[geocoding] OSRM respondio ${res.status} para la ruta`, origin, destination);
      return null;
    }
    const data = await res.json();
    const meters = data?.routes?.[0]?.distance;
    if (!Number.isFinite(meters)) {
      console.warn("[geocoding] OSRM no devolvio distancia valida:", data);
      return null;
    }
    return Math.round(meters / 100) / 10; // km con 1 decimal
  } catch (e) {
    console.warn("[geocoding] Error calculando ruta OSRM:", e);
    return null;
  }
}

/**
 * Geocodifica una direccion y calcula los km de ruta desde ORIGIN.
 * @param {string} address
 * @returns {Promise<{lat:number, lng:number, kmFromOrigin:number}|null>}
 */
export async function getKmFromOrigin(address) {
  const coords = await geocodeAddress(address);
  if (!coords) return null;

  const kmFromOrigin = await getDrivingDistanceKm(ORIGIN, coords);
  if (kmFromOrigin === null) return null;

  return { lat: coords.lat, lng: coords.lng, kmFromOrigin };
}
